import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, Calendar, Users, FileText, Wallet, Camera, MessageSquare,
  ArrowRight, Sparkles, Check, Zap, Smartphone, BarChart3, Mail, Quote,
} from "lucide-react";
import Diagnostic from "../components/Diagnostic";
import { trackMetaEvent } from "../lib/metaPixel";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

// ─── 6 piliers (Dépenses photo en 1ère position) ─────────────────────────
const pillars = [
  {
    icon: Camera,
    title: "Dépenses par photo",
    desc: "Photographiez un reçu (essence, formation, matériel…) — l'IA extrait fournisseur, montant, TVA, catégorie.",
    tag: "Cerise sur le gâteau 🍒",
    star: true,
  },
  {
    icon: Calendar,
    title: "Agenda intégré",
    desc: "Vue mois / semaine / jour, sur tous vos écrans. Synchronisable avec Google Calendar.",
    tag: "+ Rappels SMS auto",
  },
  {
    icon: Users,
    title: "Fiches clients sécurisées",
    desc: "Coordonnées, motif d'accompagnement, objectifs, notes de séance. Données chiffrées au repos.",
    tag: "PDF export en 1 clic",
  },
  {
    icon: FileText,
    title: "Factures avec mentions légales",
    desc: "PDF prêt à envoyer en 30 secondes. Mentions applicables à votre situation (dont art. 293 B CGI si franchise en base). Séance créée automatiquement.",
    tag: "Zéro double saisie",
  },
  {
    icon: MessageSquare,
    title: "Rappels SMS auto",
    desc: "Vos clients reçoivent un rappel SMS la veille. Moins d'oublis, moins de no-shows. Via Twilio (à votre nom).",
    tag: "Configurable en 2 min",
  },
];

const professions = [
  "Sophrologues", "Naturopathes", "Hypnothérapeutes", "Ostéopathes",
  "Réflexologues", "Coachs bien-être", "Énergéticiens", "Praticiens Reiki",
  "Massage bien-être", "Diététiciens", "Nutritionnistes", "Psychothérapeutes",
  "Praticiens EFT", "Psychomotriciens",
];

// ─── 4 témoignages — chacun illustre une essence différente de l'outil ──
const testimonials = [
  {
    initials: "ÉL",
    name: "Élise",
    role: "Naturopathe",
    city: "Bordeaux",
    angle: "Outil évolutif",
    quote: "L'outil est déjà top, ça change vraiment ma vie au quotidien. Mais ce qui est encore plus dingue, c'est que l'équipe écoute nos retours et étoffe l'app à notre demande — sans surcoût. J'ai suggéré une fonctionnalité, elle est arrivée en quelques jours. La réactivité que les gros SaaS ne peuvent pas se permettre.",
  },
  {
    initials: "CD",
    name: "Capucine",
    role: "Diététicienne",
    city: "Poitiers",
    angle: "Fiches clients chiffrées",
    quote: "Toutes les infos de mes clients (coordonnées, objectifs, notes de séance) sont enfin centralisées. Plus de cahier qui traîne. Et en séance, j'ai tout l'historique en 2 clics.",
  },
  {
    initials: "MR",
    name: "Murielle",
    role: "Psychomotricienne",
    city: "Roanne",
    angle: "Agenda + Rappels SMS",
    quote: "Mon planning et mes rappels SMS automatiques ont divisé mes rendez-vous oubliés par trois. Les clients adorent, et moi je ne passe plus mes soirées à envoyer des messages manuels.",
  },
  {
    initials: "JL",
    name: "Julien",
    role: "Psychothérapeute",
    city: "Lyon",
    angle: "URSSAF & Net réel",
    quote: "Pour la première fois en 8 ans d'exercice, je sais exactement ce que je gagne chaque mois après URSSAF. Plus de mauvaise surprise au trimestre — je peux enfin piloter mon cabinet.",
  },
];

const faqs = [
  {
    q: "Est-ce que je dois installer quelque chose ?",
    a: "Non. Mon Cabinet Bien-être fonctionne directement depuis votre navigateur, sur iPhone, Android ou ordinateur. Aucune installation, aucune mise à jour manuelle. Vous ouvrez, vous travaillez.",
  },
  {
    q: "Que se passe-t-il après les 14 jours de garantie ?",
    a: "Votre abonnement 19,99 €/mois est actif dès la souscription, sans engagement. Si vous n'êtes pas satisfait·e, un simple email dans les 14 jours suivant votre premier paiement suffit pour être remboursé·e intégralement. Passé ce délai, vous restez libre de résilier à tout moment.",
  },
  {
    q: "Comment résilier si je ne suis pas satisfait ?",
    a: "Depuis votre espace personnel, en un clic, à tout moment. Pas de formulaire, pas d'attente, pas de rétention. Et si vous résiliez dans les 14 jours suivant votre premier paiement, vous êtes remboursé·e intégralement.",
  },
  {
    q: "Mes données clients sont-elles en sécurité ?",
    a: "Les données sont chiffrées au repos et en transit. L'app est conçue pour accompagner le respect de vos obligations RGPD. Vos dossiers ne sont accessibles qu'à vous. Nous ne revendons aucune donnée. Vous pouvez exporter vos données à tout moment au format PDF.",
  },
  {
    q: "Je ne suis pas thérapeute mais praticien du bien-être. Est-ce que ça marche pour moi ?",
    a: "Oui. Mon Cabinet Bien-être est conçu pour tous les praticiens indépendants du bien-être en France : sophrologues, naturopathes, hypnothérapeutes, ostéopathes, coachs, énergéticiens, praticiens Reiki, diététiciens, nutritionnistes, psychomotriciens, et bien d'autres.",
  },
  {
    q: "Je n'y connais rien en comptabilité. Est-ce que je vais m'en sortir ?",
    a: "C'est exactement pour vous que l'outil a été créé. Pas de jargon comptable, pas de formation nécessaire. Vous photographiez vos reçus, l'IA fait le reste. Vous voyez votre net réel automatiquement. Aucune connaissance préalable requise.",
  },
  {
    q: "Les rappels SMS sont-ils inclus dans le prix ?",
    a: "Les rappels SMS fonctionnent via votre compte Twilio (à votre nom). L'intégration est incluse dans l'abonnement et se configure en 2 minutes. Le coût des SMS Twilio est très bas (quelques centimes par message) — c'est Twilio qui vous les facture directement, pas Capsule Patrimoine.",
  },
  {
    q: "J'ai une question que je ne trouve pas ici. Comment vous contacter ?",
    a: "Écrivez-nous directement à contact@capsulepatrimoine.com. Nous répondons sous 24h.",
  },
];

// ─── Hero Mockup : une mini-version stylée du dashboard ──────────────────
function HeroMockup() {
  return (
    <div className="relative" data-testid="hero-mockup">
      {/* Floating sage glow */}
      <div className="absolute -inset-6 bg-sage/10 rounded-[40px] blur-3xl -z-10" />

      <div className="card-soft p-5 sm:p-6 shadow-[0_20px_60px_rgba(123,140,122,0.18)]" style={{ backgroundColor: "#FFFFFF" }}>
        {/* Mock header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-sage flex items-center justify-center">
              <Leaf className="w-3.5 h-3.5 text-white" strokeWidth={1.6}/>
            </div>
            <div className="font-display text-sm">Mon Cabinet</div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-sage"></div>
            <div className="text-[10px] text-muted-soft">Mars 2026</div>
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MiniStat label="Encaissé" value="4 280 €" sub="↑ 12%" tone="sage" />
          <MiniStat label="URSSAF" value="980 €" sub="estimé" tone="muted" />
          <MiniStat label="Net réel" value="3 050 €" sub="ce mois" tone="primary" />
        </div>

        {/* Mini chart bars */}
        <div className="bg-sage-light/50 rounded-2xl p-4 mb-4">
          <div className="flex items-end justify-between gap-1.5 h-20">
            {[40, 65, 50, 80, 55, 70, 90, 75, 95, 60, 85, 100].map((h, i) => (
              <div key={i} className="flex-1 rounded-t-md bg-sage"
                style={{ height: `${h}%`, opacity: 0.35 + (h / 100) * 0.6 }} />
            ))}
          </div>
          <div className="text-[10px] text-muted-soft mt-2 text-center">12 derniers mois</div>
        </div>

        {/* Upcoming sessions */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-soft font-medium">Prochaines séances</div>
          <MiniRow time="14:00" name="Sophie M." type="Suivi" />
          <MiniRow time="15:30" name="Pierre L." type="1er RDV" highlight />
          <MiniRow time="17:00" name="Anna B." type="Visio" />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, tone = "muted" }) {
  const colors = {
    sage: "text-sage",
    muted: "text-muted-soft",
    primary: "text-sage",
  };
  return (
    <div className="bg-[#FAF9F6] rounded-xl p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-muted-soft">{label}</div>
      <div className={`font-display text-sm sm:text-base font-medium ${colors[tone] || ""}`}>{value}</div>
      <div className="text-[9px] text-muted-soft">{sub}</div>
    </div>
  );
}

function MiniRow({ time, name, type, highlight = false }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${highlight ? "bg-sage-light" : "bg-[#FAF9F6]"}`}>
      <div className={`text-[10px] font-mono ${highlight ? "text-sage font-bold" : "text-muted-soft"}`}>{time}</div>
      <div className="flex-1">
        <div className="text-xs font-medium">{name}</div>
      </div>
      <div className="text-[10px] text-muted-soft">{type}</div>
    </div>
  );
}

// ─── MAIN LANDING ────────────────────────────────────────────────────────
export default function Landing() {
  const scrollTo = (id) => (e) => {
    e?.preventDefault?.();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    // Meta: ViewContent — engaged view of the sales page
    const t = setTimeout(() => {
      try {
        trackMetaEvent("ViewContent", {
          content_name: "Landing — Mon Cabinet Bien-être",
          content_type: "product",
          value: 19.99,
          currency: "EUR",
        });
      } catch (_) { /* ignore */ }
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg)" }} data-testid="landing-page">
      {/* ── Nav ──────────────────────────────────────────────── */}
      <nav className="px-6 sm:px-10 py-5 flex items-center justify-between max-w-[1400px] mx-auto">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-10 h-10 rounded-2xl bg-sage flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display text-base leading-tight">Mon Cabinet Bien-être</div>
            <div className="text-xs text-muted-soft">by Capsule Patrimoine</div>
          </div>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/install" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-soft hover:text-sage" data-testid="nav-install">
            <Smartphone className="w-3.5 h-3.5"/> Installer
          </Link>
          <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">Connexion</Link>
          <Link to="/register" className="btn-sage text-sm" data-testid="nav-register">
            Démarrer
          </Link>
        </div>
      </nav>

      {/* ════════════ SECTION 1 — HERO ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1400px] mx-auto pt-8 lg:pt-12 pb-12 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: copy + CTA */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage-light mb-5">
              <Sparkles className="w-3.5 h-3.5 text-sage" strokeWidth={1.6} />
              <span className="text-xs text-sage font-medium">Pensé pour les thérapeutes français</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Vous accompagnez <br className="hidden lg:block"/>vos clients.<br/>
              <span className="text-sage">Pas vos tableurs.</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-soft mb-4 leading-relaxed">
              Agenda, fiches clients, factures avec mentions légales, rappels SMS — et même vos dépenses
              saisies par <b>simple photo</b>. Tout votre cabinet, dans une seule app.
              <span className="block mt-1.5 text-sage font-medium">Vraiment tout.</span>
            </p>

            <p className="text-xs text-muted-soft mb-7 italic">
              Pour sophrologues · naturopathes · hypnothérapeutes · coachs · ostéopathes · et toute profession bien-être en France
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-sage text-white font-medium px-6 py-3.5 rounded-2xl text-sm sm:text-base hover:bg-[#5F6E5F] transition-all shadow-[0_12px_40px_rgba(123,140,122,0.35)]"
                data-testid="hero-cta-start"
              >
                <Zap className="w-4 h-4" strokeWidth={2}/>
                S'abonner — 19,99 €/mois · Garantie 14 jours
                <ArrowRight className="w-4 h-4"/>
              </Link>
              <a
                href="#diagnostic"
                onClick={scrollTo("diagnostic")}
                className="inline-flex items-center justify-center gap-2 bg-sage-light text-sage font-medium px-6 py-3.5 rounded-2xl text-sm sm:text-base hover:bg-[#DAE3D9] transition-all border border-sage/20"
                data-testid="hero-cta-diagnostic"
              >
                <BarChart3 className="w-4 h-4" strokeWidth={2}/>
                Score financier gratuit
                <ArrowRight className="w-4 h-4"/>
              </a>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-soft">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-sage" strokeWidth={2.5}/>Satisfait ou remboursé 14 jours</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-sage" strokeWidth={2.5}/>Données chiffrées au repos</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-sage" strokeWidth={2.5}/>iPhone + Android + ordi</span>
            </div>
          </div>

          {/* Right: app mockup */}
          <div className="lg:pl-8">
            <HeroMockup />
          </div>
        </div>
      </section>

      {/* ════════════ SECTION 2 — SCORE FINANCIER ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1100px] mx-auto pt-8 sm:pt-12" id="diagnostic">
        <div className="text-center mb-2">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Bonus offert</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">
            Et au fait — savez-vous <u>vraiment</u> ce que vous gagnez ?
          </h2>
          <p className="text-muted-soft max-w-2xl mx-auto leading-relaxed">
            Entre l'URSSAF, le seuil TVA et les charges cachées, votre net réel surprend souvent.
            Faites le diagnostic gratuit — 30 secondes, aucune inscription.
          </p>
        </div>
      </section>

      <Diagnostic />

      {/* ════════════ SECTION 3 — FONCTIONNALITÉS ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1400px] mx-auto py-14 sm:py-20" id="features">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Les 5 piliers de votre nouveau cabinet</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">Conçu pour faire disparaître la paperasse.</h2>
          <p className="text-muted-soft leading-relaxed">
            Pas un de plus, pas un de moins. Tout ce qu'un cabinet bien-être a vraiment besoin, sans surcouche inutile.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              className={`card-soft card-soft-hover p-7 relative overflow-hidden ${p.star ? "ring-2 ring-sage/40" : ""}`}
              data-testid={`pillar-${i}`}
              style={p.star ? { background: "linear-gradient(135deg, #FFFFFF 0%, var(--sage-light) 100%)" } : {}}
            >
              {p.star && (
                <div className="absolute top-3 right-3 text-[10px] uppercase tracking-wider text-white font-semibold bg-sage px-2.5 py-1 rounded-full">
                  ⭐ Star feature
                </div>
              )}
              <div className="w-12 h-12 rounded-xl bg-sage-light flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5 text-sage" strokeWidth={1.6} />
              </div>
              <div className="font-display text-lg mb-2">{p.title}</div>
              <div className="text-sm text-muted-soft leading-relaxed mb-4">{p.desc}</div>
              <div className="inline-flex items-center gap-1.5 text-xs text-sage font-medium">
                <Check className="w-3.5 h-3.5" strokeWidth={2.5}/>{p.tag}
              </div>
            </div>
          ))}
        </div>

        {/* Cerise sur le gâteau — OCR Démo */}
        <div className="card-soft p-8 sm:p-12 lg:p-16 overflow-hidden relative" style={{ background: "linear-gradient(135deg, #FAF9F6 0%, var(--sage-light) 100%)" }}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-sage font-semibold mb-4">
                🍒 La cerise sur le gâteau
              </div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-5">
                Vous prenez un reçu en photo.<br/>
                <span className="text-sage">Vous ne touchez plus à rien.</span>
              </h3>
              <p className="text-muted-soft leading-relaxed mb-6 text-base">
                Essence, formation, matériel, repas pro… À chaque dépense, photo → l'IA lit le ticket
                et remplit <b>fournisseur, montant TTC, HT, TVA, catégorie, date</b>. Vous validez en 2 secondes.
              </p>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>14 catégories</b> de dépenses proposées pour faciliter votre classement</span></li>
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>Déduction automatique</b> du net réel</span></li>
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>Plus jamais de saisie comptable</b> manuelle en fin de mois</span></li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:gap-5">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-soft font-medium mb-2 text-center">📷 Vous</div>
                <div className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.06)] aspect-[3/4] flex flex-col items-center justify-center text-center">
                  <Camera className="w-10 h-10 text-muted-soft mb-3" strokeWidth={1.4} />
                  <div className="text-xs text-muted-soft leading-relaxed">
                    <div className="font-mono text-[10px] mb-2 text-sage">TOTAL ENERGIE</div>
                    <div className="font-mono text-[10px]">85, av. République</div>
                    <div className="font-mono text-[10px] mb-2">75011 PARIS</div>
                    <div className="font-mono text-[10px]">14/05/2026 · 09:47</div>
                    <div className="font-mono text-[10px] mt-2">SP95 · 35,12 L</div>
                    <div className="font-mono text-sm font-bold text-sage mt-1.5">68,40 €</div>
                  </div>
                </div>
                <div className="text-center text-xs text-muted-soft mt-2 italic">photo du ticket</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-sage font-semibold mb-2 text-center">✨ L'app</div>
                <div className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.06)] aspect-[3/4] flex flex-col gap-2.5 text-xs">
                  <Row label="Fournisseur" value="Total Énergie" highlight />
                  <Row label="Date" value="14 mai 2026" />
                  <Row label="Catégorie" value="🚗 Carburant" />
                  <Row label="TTC" value="68,40 €" highlight />
                  <Row label="HT" value="57,00 €" />
                  <Row label="TVA 20 %" value="11,40 €" />
                  <div className="mt-auto pt-2 border-t border-border">
                    <div className="text-[10px] text-sage font-semibold uppercase tracking-wider text-center">✓ Enregistré</div>
                  </div>
                </div>
                <div className="text-center text-xs text-sage font-medium mt-2">en 2 secondes</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ SECTION 4 — POUR QUI ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1100px] mx-auto py-10 sm:py-14">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Mon Cabinet Bien-être est pour vous si vous êtes</div>
          <h2 className="font-display text-2xl sm:text-3xl">Praticienne ou praticien indépendant·e du bien-être.</h2>
        </div>
        <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
          {professions.map((p) => (
            <span key={p} className="px-4 py-2 rounded-full bg-white border border-border text-sm text-muted-soft hover:border-sage hover:text-sage transition-colors">
              {p}
            </span>
          ))}
          <span className="px-4 py-2 rounded-full bg-sage-light text-sm text-sage font-medium">
            …et toute profession bien-être en France
          </span>
        </div>
      </section>

      {/* ════════════ SECTION 4bis — URSSAF & Net réel (feature signature) ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1100px] mx-auto py-14 sm:py-20" id="urssaf">
        <div className="card-soft p-8 sm:p-12 lg:p-16 overflow-hidden relative" style={{ background: "linear-gradient(135deg, #FFFFFF 0%, var(--sage-light) 50%, #FFFFFF 100%)" }}>
          {/* decorative wallet */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-sage/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-sage/5 blur-3xl" />

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12 items-center relative">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-sage font-semibold mb-4">
                💰 L'argument qui change tout
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-5">
                URSSAF & Net réel,<br/>
                <span className="text-sage">calculés pour vous chaque mois.</span>
              </h2>
              <p className="text-muted-soft leading-relaxed mb-6 text-base">
                Plus jamais de surprise au trimestre. Vous voyez en direct combien vous gagnez
                <b> vraiment</b>, après URSSAF, charges et dépenses. Un logiciel cabinet
                qui vous donne l'info claire, en temps réel.
              </p>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>Calcul automatique</b> selon votre régime URSSAF 2026 : <b>25,6 %</b> pour les professions libérales non réglementées hors Cipav et <b>23,2 %</b> pour les professions relevant de la Cipav</span></li>
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>Net réel mensuel</b> = Encaissé − URSSAF − Dépenses pro</span></li>
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>Suivi mensuel & annuel</b> avec graphique sur 12 mois</span></li>
                <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-sage flex-shrink-0" strokeWidth={2}/><span><b>Alertes seuils</b> TVA et plafonds micro-entreprise</span></li>
              </ul>
            </div>

            {/* Visual mockup : ce qu'il vous reste */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 shadow-[0_20px_60px_rgba(123,140,122,0.18)]">
                <div className="text-[10px] uppercase tracking-wider text-muted-soft font-medium mb-3 text-center">Votre mois de mars</div>
                <div className="space-y-2.5">
                  <UrssafLine label="Encaissé" value="4 280 €" tone="muted" />
                  <UrssafLine label="− URSSAF" value="−1 095 €" tone="warm" />
                  <UrssafLine label="− Dépenses pro" value="−320 €" tone="warm" />
                  <div className="h-px bg-border my-2"></div>
                  <div className="flex items-center justify-between px-3 py-3 rounded-xl bg-sage-light">
                    <div className="text-xs font-semibold text-sage uppercase tracking-wider">Net réel</div>
                    <div className="font-display text-2xl text-sage font-medium">2 865 €</div>
                  </div>
                </div>
                <div className="text-center text-[10px] text-muted-soft mt-3 italic">Mis à jour automatiquement</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ SECTION — Témoignages ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1300px] mx-auto py-14 sm:py-20" id="testimonials">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Avis de nos bêta-testeurs</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">Ils ont adopté Mon Cabinet.</h2>
          <p className="text-muted-soft leading-relaxed">
            Chacun y trouve ce qui change tout pour lui — voici leurs mots.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 lg:gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className="card-soft card-soft-hover p-6 sm:p-7 relative overflow-hidden"
              data-testid={`testimonial-${i}`}
            >
              <Quote className="absolute top-4 right-4 w-8 h-8 text-sage/15" strokeWidth={1.5}/>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-sage flex items-center justify-center text-white font-display text-sm flex-shrink-0">
                  {t.initials}
                </div>
                <div>
                  <div className="font-display text-sm">{t.name}</div>
                  <div className="text-xs text-muted-soft">{t.role} · {t.city}</div>
                </div>
              </div>

              <p className="text-sm text-foreground/85 leading-relaxed italic mb-4">
                « {t.quote} »
              </p>

              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-sage font-semibold bg-sage-light px-2.5 py-1 rounded-full">
                <Sparkles className="w-3 h-3" strokeWidth={1.8}/> {t.angle}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[11px] text-muted-soft italic mt-6">
          Témoignages de bêta-testeurs ayant participé à la phase pilote de Mon Cabinet Bien-être.
        </p>
      </section>

      {/* ════════════ SECTION — Comparatif concurrents ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1100px] mx-auto py-14 sm:py-20" id="compare">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Comparatif</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">Un outil pensé <u>vraiment</u> pour vous.</h2>
          <p className="text-muted-soft max-w-2xl mx-auto leading-relaxed">
            Comparatif indicatif basé sur les informations publiques des éditeurs au 1er trimestre 2026 — à vérifier selon l'offre choisie chez chaque acteur.
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block card-soft p-2 overflow-hidden">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead>
              <tr>
                <th className="p-4 text-left font-medium text-muted-soft w-1/3"></th>
                <th className="p-4 text-center bg-sage-light rounded-t-xl border-b-2 border-sage">
                  <div className="font-display text-base text-sage">Mon Cabinet</div>
                  <div className="text-[10px] text-sage uppercase tracking-wider mt-0.5">Bien-être</div>
                </th>
                <th className="p-4 text-center font-display text-sm text-muted-soft">Doctolib</th>
                <th className="p-4 text-center font-display text-sm text-muted-soft">Nomisora</th>
                <th className="p-4 text-center font-display text-sm text-muted-soft">Jupi'Terre</th>
              </tr>
            </thead>
            <tbody>
              <CompareRow label="Prix mensuel" values={["19,99 €", "139 €+", "14,99 €", "29 à 49 €"]} pricing />
              <CompareRow label="Agenda + RDV" values={[true, true, false, true]} />
              <CompareRow label="Fiches clients chiffrées" values={[true, false, false, true]} />
              <CompareRow label="Factures avec mentions légales" values={[true, false, true, true]} />
              <CompareRow label="Dépenses par photo (IA)" values={[true, false, false, false]} />
              <CompareRow label="URSSAF & Net réel auto" values={[true, false, false, false]} />
              <CompareRow label="Rappels SMS inclus" values={[true, true, false, false]} />
              <CompareRow label="Pensé pour le bien-être" values={[true, false, true, true]} last />
            </tbody>
          </table>
        </div>

        {/* Mobile stacked */}
        <div className="md:hidden space-y-4">
          {[
            { label: "Prix mensuel", values: { mc: "19,99 €", doctolib: "139 €+", nomisora: "14,99 €", jupi: "29-49 €" } },
            { label: "Agenda + RDV", values: { mc: true, doctolib: true, nomisora: false, jupi: true } },
            { label: "Fiches clients chiffrées", values: { mc: true, doctolib: false, nomisora: false, jupi: true } },
            { label: "Factures avec mentions légales", values: { mc: true, doctolib: false, nomisora: true, jupi: true } },
            { label: "Dépenses par photo (IA)", values: { mc: true, doctolib: false, nomisora: false, jupi: false } },
            { label: "URSSAF & Net réel", values: { mc: true, doctolib: false, nomisora: false, jupi: false } },
            { label: "Rappels SMS inclus", values: { mc: true, doctolib: true, nomisora: false, jupi: false } },
            { label: "Pensé pour le bien-être", values: { mc: true, doctolib: false, nomisora: true, jupi: true } },
          ].map((row, i) => (
            <div key={i} className="card-soft p-4">
              <div className="text-xs font-semibold mb-2.5">{row.label}</div>
              <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
                <CompareCell value={row.values.mc} name="Mon Cabinet" highlight />
                <CompareCell value={row.values.doctolib} name="Doctolib" />
                <CompareCell value={row.values.nomisora} name="Nomisora" />
                <CompareCell value={row.values.jupi} name="Jupi'Terre" />
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[11px] text-muted-soft italic mt-5">
          Comparatif basé sur les informations publiques des éditeurs au 1er trimestre 2026. Tarifs hors options.
        </p>
      </section>

      {/* ════════════ SECTION 5 — PRICING ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1100px] mx-auto py-14 sm:py-20" id="pricing">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Tarif</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-3">Simple. Transparent. Sans surprise.</h2>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="card-soft p-8 sm:p-12 text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--surface) 0%, var(--sage-light) 100%)" }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage text-white text-[11px] uppercase tracking-wider font-semibold mb-6">
              <Sparkles className="w-3 h-3"/> Offre de lancement
            </div>

            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="font-display text-6xl sm:text-7xl text-sage font-medium">19,99 €</span>
              <span className="text-muted-soft text-base">/mois</span>
            </div>
            <p className="text-xs text-muted-soft mb-8">
              19,99 € TTC · Sans engagement · Résiliable à tout moment
            </p>

            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-sage text-white font-medium px-8 py-4 rounded-2xl text-base hover:bg-[#5F6E5F] transition-all shadow-[0_12px_40px_rgba(123,140,122,0.35)] mb-3"
              data-testid="pricing-cta-register"
            >
              <Zap className="w-5 h-5" strokeWidth={2}/>
              Démarrer mon abonnement
              <ArrowRight className="w-5 h-5"/>
            </Link>
            <div className="text-xs text-sage font-medium mb-8">✓ Garantie satisfait ou remboursé pendant 14 jours</div>

            <ul className="text-left space-y-3 max-w-md mx-auto">
              {[
                "Agenda illimité + synchronisation Google Calendar",
                "Rappels SMS automatiques inclus (via Twilio)",
                "Fiches clients illimitées · données chiffrées au repos",
                "Factures avec mentions légales (dont art. 293 B CGI si franchise en base)",
                "Dépenses par photo (IA incluse)",
                "Calcul URSSAF & net réel en temps réel",
                "Accès iPhone + Android + ordinateur",
                "Mises à jour incluses",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-sm">
                  <div className="w-5 h-5 rounded-full bg-sage flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" strokeWidth={3}/>
                  </div>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ════════════ SECTION 6 — FAQ ════════════ */}
      <section className="px-6 sm:px-10 max-w-[900px] mx-auto py-14 sm:py-20" id="faq">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-3">Vous vous demandez peut-être…</div>
          <h2 className="font-display text-3xl sm:text-4xl">Foire aux questions</h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="card-soft border-0 px-5 sm:px-6 [&[data-state=open]]:bg-white"
              data-testid={`faq-${i}`}
            >
              <AccordionTrigger className="text-left font-display text-base sm:text-lg py-5 hover:no-underline hover:text-sage [&[data-state=open]]:text-sage">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-soft leading-relaxed pb-5 pt-1">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="text-center mt-8 text-sm text-muted-soft">
          <Mail className="w-4 h-4 inline mr-1.5 -mt-0.5 text-sage"/>
          Une autre question ? <a href="mailto:contact@capsulepatrimoine.com" className="text-sage hover:underline font-medium">contact@capsulepatrimoine.com</a>
        </div>
      </section>

      {/* ════════════ SECTION 7 — CTA FINAL ════════════ */}
      <section className="px-6 sm:px-10 max-w-[1100px] mx-auto py-14 sm:py-20">
        <div className="card-soft p-10 sm:p-14 lg:p-16 text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--sage) 0%, #5F6E5F 100%)" }}>
          {/* Subtle decorative leaf */}
          <Leaf className="absolute -top-6 -right-6 w-32 h-32 text-white/10 rotate-12" strokeWidth={1}/>
          <Leaf className="absolute -bottom-6 -left-6 w-24 h-24 text-white/10 -rotate-45" strokeWidth={1}/>

          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">
            Votre cabinet mérite mieux<br/>
            <span className="opacity-90">qu'un tableur Excel.</span>
          </h2>
          <p className="text-white/85 text-base sm:text-lg mb-10 max-w-xl mx-auto">
            19,99 €/mois · Satisfait ou remboursé sous 14 jours. Sans condition.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-white text-sage font-semibold px-7 py-3.5 rounded-2xl text-sm sm:text-base hover:bg-[#FAF9F6] transition-all shadow-[0_12px_40px_rgba(0,0,0,0.15)]"
              data-testid="final-cta-start"
            >
              <Zap className="w-4 h-4" strokeWidth={2.2}/>
              Démarrer maintenant
              <ArrowRight className="w-4 h-4"/>
            </Link>
            <a
              href="#diagnostic"
              onClick={scrollTo("diagnostic")}
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-medium px-7 py-3.5 rounded-2xl text-sm sm:text-base hover:bg-white/20 transition-all border border-white/30"
              data-testid="final-cta-diagnostic"
            >
              <BarChart3 className="w-4 h-4" strokeWidth={2}/>
              Score financier gratuit
              <ArrowRight className="w-4 h-4"/>
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-white/80">
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" strokeWidth={2.5}/>Satisfait ou remboursé 14 jours</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" strokeWidth={2.5}/>Résiliation en 1 clic</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" strokeWidth={2.5}/>Données chiffrées</span>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="px-6 sm:px-10 py-10 text-center text-xs text-muted-soft border-t mt-4 space-y-2" style={{ borderColor: "var(--border)" }}>
        <div>
          Mon Cabinet Bien-être — édité par Capsule Patrimoine · SIRET 484 280 771 00060 · Contact : <a href="mailto:contact@capsulepatrimoine.com" className="text-sage hover:underline">contact@capsulepatrimoine.com</a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <Link to="/politique-remboursement" className="text-sage hover:underline" data-testid="footer-refund-link">
            Politique de remboursement
          </Link>
          <span className="text-muted-soft">·</span>
          <Link to="/politique-confidentialite" className="text-sage hover:underline" data-testid="footer-privacy-link">
            Politique de confidentialité & RGPD
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Row({ label, value, highlight = false }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-soft">{label}</div>
      <div className={`text-xs font-medium ${highlight ? "text-sage" : "text-foreground"}`}>{value}</div>
    </div>
  );
}

function UrssafLine({ label, value, tone = "muted" }) {
  const valueColor = {
    muted: "text-foreground",
    warm: "text-[#B58B5C]",
    sage: "text-sage font-semibold",
  }[tone];
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <div className="text-xs text-muted-soft">{label}</div>
      <div className={`text-sm font-medium tabular-nums ${valueColor}`}>{value}</div>
    </div>
  );
}

function CompareRow({ label, values, pricing = false, last = false }) {
  return (
    <tr className={last ? "" : "border-b border-border/50"}>
      <td className="p-4 text-sm text-muted-soft">{label}</td>
      {values.map((v, idx) => {
        const isMc = idx === 0;
        return (
          <td
            key={idx}
            className={`p-4 text-center ${isMc ? "bg-sage-light/50 border-l border-r border-sage/20" : ""} ${isMc && last ? "rounded-b-xl border-b border-sage/20" : ""}`}
          >
            {pricing ? (
              <span className={`font-medium ${isMc ? "text-sage" : "text-foreground"}`}>{v}</span>
            ) : v === true ? (
              <Check className={`w-5 h-5 mx-auto ${isMc ? "text-sage" : "text-sage/70"}`} strokeWidth={2.5}/>
            ) : (
              <span className="text-muted-soft/40 text-base">✗</span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function CompareCell({ value, name, highlight = false }) {
  const isBool = typeof value === "boolean";
  return (
    <div className={`rounded-lg p-2 ${highlight ? "bg-sage-light" : "bg-[#FAF9F6]"}`}>
      <div className={`text-[9px] uppercase tracking-wider ${highlight ? "text-sage font-semibold" : "text-muted-soft"} mb-1`}>{name}</div>
      <div className={`text-xs font-medium ${highlight ? "text-sage" : "text-foreground"}`}>
        {isBool ? (value ? <Check className="w-4 h-4 mx-auto" strokeWidth={2.5}/> : <span className="opacity-30">✗</span>) : value}
      </div>
    </div>
  );
}
